import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Tab,
  TabList,
  Title2,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { useAuth, VersionBanner } from '@warehouselogix/web-common';
import { Suspense, lazy } from 'react';
import type { ReactNode } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { UnsavedGuardProvider, useConfirmLeave } from './components/unsavedGuard';
import { isTenantsVisible, usePlatformAccess } from './platform/usePlatformAccess';
import { CurrentTenantProvider, useCurrentTenant } from './tenant/CurrentTenantContext';
import type { PickerState } from './tenant/CurrentTenantContext';
import { RoleBadge, ViaBadge } from './tenant/RoleBadge';
import { TenantPicker } from './tenant/TenantPicker';
import { TenantSwitcher } from './tenant/TenantSwitcher';
import {
  MyTenantsContext,
  findTenantBySlug,
  tenantPath,
  useMyTenants,
  useMyTenantsLoader,
} from './tenant/useMyTenants';

// One chunk per screen — the Site Config admin never downloads the platform
// console and vice versa.
const TenantsScreen = lazy(() =>
  import('./screens/TenantsScreen').then((m) => ({ default: m.TenantsScreen }))
);
const SiteConfigScreen = lazy(() =>
  import('./screens/SiteConfigScreen').then((m) => ({ default: m.SiteConfigScreen }))
);
const ToolsScreen = lazy(() =>
  import('./screens/ToolsScreen').then((m) => ({ default: m.ToolsScreen }))
);
const EmployeesScreen = lazy(() =>
  import('./screens/EmployeesScreen').then((m) => ({ default: m.EmployeesScreen }))
);
const DevicesScreen = lazy(() =>
  import('./screens/DevicesScreen').then((m) => ({ default: m.DevicesScreen }))
);
const ExtClientsScreen = lazy(() =>
  import('./screens/ExtClientsScreen').then((m) => ({ default: m.ExtClientsScreen }))
);
const MembersScreen = lazy(() =>
  import('./screens/MembersScreen').then((m) => ({ default: m.MembersScreen }))
);

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXL),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  headerGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  body: {
    display: 'flex',
    flexGrow: 1,
  },
  nav: {
    minWidth: '220px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalS),
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
  },
  main: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXXL),
  },
  suspense: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    paddingTop: tokens.spacingVerticalXXXL,
  },
});

/** The per-tenant screens, in nav order. `segment` is the path piece after `/t/:slug/`. */
const TENANT_SCREENS: ReadonlyArray<{ segment: string; label: string }> = [
  { segment: 'site-config', label: 'Site Config' },
  { segment: 'tools', label: 'Tools' },
  { segment: 'employees', label: 'Employees' },
  { segment: 'devices', label: 'Devices' },
  { segment: 'api-clients', label: 'API Clients' },
  { segment: 'members', label: 'Members' },
];

/** Nav value for "go to the picker on purpose" (no auto-redirect). */
const PICKER_NAV = '/';
const PLATFORM_NAV = '/tenants';

interface NavItem {
  path: string;
  label: string;
}

/** Deep-link fail-closed backstop for /tenants when the probe denied. */
function TenantsDenied(): JSX.Element {
  return (
    <MessageBar intent="warning">
      <MessageBarBody>
        <MessageBarTitle>No platform access</MessageBarTitle>
        Tenant management needs platform-administrator access, which this account does not have.
      </MessageBarBody>
    </MessageBar>
  );
}

/**
 * The six tenant screens under one CurrentTenantProvider (`/t/:slug/*`).
 * Keyed by slug so a switch remounts the screens — no dialog or draft state
 * from tenant A survives into tenant B.
 */
function TenantArea(): JSX.Element {
  const { slug = '' } = useParams<{ slug: string }>();
  return (
    <CurrentTenantProvider key={slug}>
      <Routes>
        <Route path="site-config" element={<SiteConfigScreen />} />
        <Route path="tools" element={<ToolsScreen />} />
        <Route path="employees" element={<EmployeesScreen />} />
        <Route path="devices" element={<DevicesScreen />} />
        <Route path="api-clients" element={<ExtClientsScreen />} />
        <Route path="members" element={<TenantMembers />} />
        <Route path="*" element={<Navigate to="site-config" replace />} />
      </Routes>
    </CurrentTenantProvider>
  );
}

/** Members under `/t/:slug/members` — the tenant id comes from the provider. */
function TenantMembers(): JSX.Element {
  const { tenant } = useCurrentTenant();
  return <MembersScreen tenantId={tenant.id} />;
}

/**
 * Members under the platform console (`/tenants/:tenantId/members`). Platform
 * admins see every tenant in `me/tenants`, so the provider resolves the UUID
 * without a grant; the same `/admin/t/:tenantId/members` endpoints serve both
 * mounts (PLAN §3 — no SECURITY DEFINER mutation functions).
 */
function PlatformTenantMembers(): JSX.Element {
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  return (
    <CurrentTenantProvider tenantId={tenantId}>
      <MembersScreen tenantId={tenantId} />
    </CurrentTenantProvider>
  );
}

function ShellBody(): JSX.Element {
  const styles = useStyles();
  const { account, logout } = useAuth();
  const my = useMyTenants();
  const platformAccess = usePlatformAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const confirmLeave = useConfirmLeave();

  // Which tenant (if any) the URL is inside, and which screen under it.
  const tenantMatch = useMatch('/t/:slug/*');
  const slug = tenantMatch?.params.slug ?? null;
  const screen = tenantMatch?.params['*'] ?? '';
  const current = slug !== null && my.data !== null ? findTenantBySlug(my.data, slug) : null;

  // PLAN §5: the zero-tenant state ("No tenants — ask your administrator")
  // renders NO nav. Nothing to navigate to until the list is known and has
  // at least one tenant (or the caller is a platform admin).
  const showNav = my.data !== null && (my.data.isPlatformAdmin || my.data.tenants.length > 0);

  const navItems: NavItem[] = [];
  if (isTenantsVisible(platformAccess)) {
    navItems.push({ path: PLATFORM_NAV, label: 'Tenants' });
  }
  navItems.push({ path: PICKER_NAV, label: 'Choose tenant' });
  if (slug !== null) {
    for (const s of TENANT_SCREENS) {
      navItems.push({ path: tenantPath(slug, s.segment), label: s.label });
    }
  }
  const selected =
    navItems.find(
      (item) =>
        item.path === location.pathname ||
        (item.path !== PICKER_NAV && location.pathname.startsWith(`${item.path}/`))
    )?.path ?? '';

  const platformGate = (element: ReactNode): ReactNode =>
    isTenantsVisible(platformAccess) ? (
      element
    ) : platformAccess === 'loading' ? (
      <div className={styles.suspense}>
        <Spinner label="Checking access…" />
      </div>
    ) : (
      <TenantsDenied />
    );

  return (
    <div className={styles.root}>
      <VersionBanner />
      <header className={styles.header}>
        <div className={styles.headerGroup}>
          <Title2>WarehouseLogix Admin{current !== null ? ` · ${current.name}` : ''}</Title2>
          {showNav && slug !== null ? <TenantSwitcher currentSlug={slug} screen={screen} /> : null}
          {current !== null ? (
            <>
              <RoleBadge role={current.role} />
              {current.via !== 'membership' ? <ViaBadge via={current.via} /> : null}
            </>
          ) : null}
        </div>
        <span>
          {account?.username ?? ''}{' '}
          <Button appearance="secondary" onClick={() => void logout()}>
            Sign out
          </Button>
        </span>
      </header>
      <div className={styles.body}>
        {showNav ? (
          <nav className={styles.nav} aria-label="Administration">
            <TabList
              vertical
              selectedValue={selected}
              onTabSelect={(_event, data) => {
                const path = String(data.value);
                if (path === location.pathname || !confirmLeave()) {
                  return;
                }
                if (path === PICKER_NAV) {
                  const state: PickerState = { explicit: true };
                  void navigate(path, { state });
                } else {
                  void navigate(path);
                }
              }}
            >
              {navItems.map((item) => (
                <Tab key={item.path} value={item.path}>
                  {item.label}
                </Tab>
              ))}
            </TabList>
          </nav>
        ) : null}
        <main className={styles.main}>
          <Suspense
            fallback={
              <div className={styles.suspense}>
                <Spinner label="Loading…" />
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<TenantPicker />} />
              <Route path="/tenants" element={platformGate(<TenantsScreen />)} />
              <Route
                path="/tenants/:tenantId/members"
                element={platformGate(<PlatformTenantMembers />)}
              />
              <Route path="/t/:slug/*" element={<TenantArea />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

/**
 * Authenticated admin shell (tenancy v1): loads `GET /me/tenants` once per
 * sign-in, then header (tenant name + switcher + role badge, account,
 * sign-out), left nav (tenant-relative tabs), routed content. The Tenants
 * surface renders ONLY when the API says platform admin — fail-closed.
 */
export function AdminShell(): JSX.Element {
  const myTenants = useMyTenantsLoader();
  return (
    <MyTenantsContext.Provider value={myTenants}>
      <UnsavedGuardProvider>
        <ShellBody />
      </UnsavedGuardProvider>
    </MyTenantsContext.Provider>
  );
}
